import type { PermissionCode } from '@cnss/shared';
import { Navigate, Outlet, useLocation } from 'react-router-dom';

import { useAuth } from '@/lib/auth-context';

interface RequireAuthProps {
  permission?: PermissionCode;
}

const CHANGE_PASSWORD_PATH = '/changer-mot-de-passe';

/**
 * Protège une branche de routes : redirige vers /connexion si non authentifié,
 * fait respecter le changement de mot de passe imposé (`mustChangePassword`)
 * avant tout accès au reste de l'application, puis vérifie la permission de la
 * route si fournie.
 *
 * Le provisionnement 2FA forcé n'a PAS besoin d'être rejoué ici : par
 * construction, l'API n'émet jamais de session pour un rôle exigeant la 2FA
 * tant qu'elle n'est pas active (voir apps/api/src/modules/auth/auth.routes.ts
 * et apps/api/src/modules/users/user-mapper.ts) — ce cas se règle entièrement
 * dans le flux de connexion (LoginPage → TwoFactorSetupPage), avant qu'une
 * session existe.
 */
export function RequireAuth({ permission }: RequireAuthProps) {
  const { status, user, hasPermission } = useAuth();
  const location = useLocation();

  if (status === 'loading') {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <div className="flex items-center gap-3 text-muted-foreground">
          <div
            className="h-5 w-5 animate-spin rounded-full border-2 border-current border-t-transparent"
            aria-hidden
          />
          <span className="text-sm">Chargement…</span>
        </div>
      </div>
    );
  }

  if (status === 'unauthenticated') {
    return <Navigate to="/connexion" replace />;
  }

  if (user?.mustChangePassword && location.pathname !== CHANGE_PASSWORD_PATH) {
    return <Navigate to={CHANGE_PASSWORD_PATH} replace />;
  }

  if (permission && !hasPermission(permission)) {
    return <Navigate to="/acces-refuse" replace />;
  }

  return <Outlet />;
}
